import {
  FC,
  ReactNode,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { breakpoints, isMobile } from 'app/spacing';
import {
  AI_PANEL_MIN_WIDTH,
  clampPanelWidth,
  maxPanelWidth,
  readStoredPanelWidth,
} from 'ts-components/PiChat/panelWidth';
import {
  motionTransition,
  usePrefersReducedMotion,
} from 'ts-components/PiChat/motion';
import {
  RAIL_INERT_PROPS,
  ResizeHandle,
  usePanelResize,
  usePanelTransition,
} from 'ts-components/PiChat/panelChrome';
import {
  getAppInsetRight,
  setAppInsetRight,
  useAppInsetRight,
} from 'ts-components/AppInset/appInset';
import { kiCodeCopy } from '../copy';

const KICODE_INSET_OWNER = 'kicode';
const KICODE_PANEL_Z_INDEX = 1035;
const BOOTSTRAP_FIXED_CONTENT_CLASS = 'is-fixed';

const Rail = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  max-width: 100vw;
  box-sizing: border-box;
  z-index: ${KICODE_PANEL_Z_INDEX};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid var(--color-border-medium);
  background: var(--color-background-white);
  box-shadow: -6px 0 16px
    color-mix(in srgb, var(--color-background-black) 4%, transparent);

  & * {
    overscroll-behavior: contain;
  }
`;

const isOverlay = () => isMobile(window.innerWidth, breakpoints.md);

const subscribeResize = (onChange: () => void) => {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
};

const currentWidth = () =>
  getAppInsetRight(KICODE_INSET_OWNER) ||
  readStoredPanelWidth(window.innerWidth);

const setWidth = (width: number | null) =>
  setAppInsetRight(KICODE_INSET_OWNER, isOverlay() ? null : width);

export interface KiCodePanelFrameProps {
  children: ReactNode | null;
}

export const KiCodePanelFrame: FC<KiCodePanelFrameProps> = ({ children }) => {
  const { t } = useTranslation();
  const copy = kiCodeCopy(t);
  const reducedMotion = usePrefersReducedMotion();
  const storeWidth = useAppInsetRight(KICODE_INSET_OWNER);
  const [exitWidth, setExitWidth] = useState(storeWidth);
  if (storeWidth > 0 && storeWidth !== exitWidth) setExitWidth(storeWidth);
  const width = storeWidth || exitWidth;

  const overlay = useSyncExternalStore(subscribeResize, isOverlay);
  const {
    resizing,
    cancel: cancelResize,
    handlers,
  } = usePanelResize(currentWidth, setWidth);
  const transition = usePanelTransition(children, reducedMotion);
  const wanted = Boolean(children);

  useLayoutEffect(() => {
    if (!wanted) return undefined;
    const fit = () => {
      if (isOverlay()) cancelResize();
      setWidth(clampPanelWidth(currentWidth(), window.innerWidth));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      cancelResize();
      setWidth(null);
    };
  }, [wanted, cancelResize]);

  if (!transition.mounted) return null;

  return (
    <Rail
      className={BOOTSTRAP_FIXED_CONTENT_CLASS}
      style={{
        width: overlay ? '100%' : width,
        transform: transition.visible ? 'none' : 'translateX(100%)',
        transition: motionTransition(['transform'], reducedMotion || resizing),
      }}
      onTransitionEnd={transition.onTransitionEnd}
      data-qa-id="kicode-panel-rail"
      {...(transition.wanted ? {} : RAIL_INERT_PROPS)}
    >
      {overlay ? null : (
        <ResizeHandle
          role="separator"
          aria-orientation="vertical"
          aria-label={copy.resize}
          aria-valuenow={width}
          aria-valuemin={AI_PANEL_MIN_WIDTH}
          aria-valuemax={maxPanelWidth(window.innerWidth)}
          tabIndex={0}
          $active={resizing}
          {...handlers}
          data-qa-id="kicode-panel-resize-handle"
        />
      )}
      {children}
    </Rail>
  );
};
